/** Shared visual shell for all 7 crew dashboard archetypes.
 *
 *  Responsibilities:
 *    · Cobrand strip — user avatar + designation + optional org
 *      logo + Verified/Pending badge. Freelancers get their union
 *      badge instead of an org logo.
 *    · Public-link share chip — copies `/u/<handle>` to clipboard.
 *    · Tabs — [Workflow · Public Profile].
 *    · Workflow grid — renders `archetype.workflow` (or the dept-
 *      specific list in `workflowByDept`) with live count badges
 *      pulled from `/api/dashboard/widget-counts`.
 *    · Quick actions strip.
 *    · Public Profile tab uses the existing <PublicProfileCard/>.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Clock3, ExternalLink, Building2, Link as LinkIcon } from "lucide-react";
import PublicProfileCard from "@/components/PublicProfileCard";

// Two-letter monogram from a company or personal name (for fallback logos).
function monogramOf(name = "") {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "??";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function CobrandStrip({ archetype, user, org }) {
  const cobranded = !!org;
  const monogram  = monogramOf(cobranded ? (org.brand_name || org.legal_name) : user.name);
  const verified  = cobranded && (org.is_verified || org.verified);
  const pending   = cobranded && !verified;
  const handle    = user.public_handle;

  const copyLink = async () => {
    if (!handle) return;
    const url = `${window.location.origin}/u/${handle}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public profile link copied", { description: url });
    } catch {
      toast.error("Copy failed — long-press the link manually");
    }
  };

  return (
    <div className="cs-card cs-grain rounded-2xl p-5 sm:p-6 mb-6" data-testid="crew-cobrand-strip">
      <div className="flex flex-wrap items-center gap-4">
        {/* Monogram / avatar chip */}
        <div className="w-14 h-14 rounded-xl flex items-center justify-center font-display text-lg tracking-wider shrink-0"
             style={{ background: `${archetype.accent}22`, color: archetype.accent }}>
          {monogram}
        </div>

        {/* Name + designation strip */}
        <div className="min-w-0 flex-1">
          <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: archetype.accent }}>
            {archetype.title}
          </div>
          <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2">
            <span className="font-display text-2xl sm:text-3xl uppercase tracking-tight text-white truncate max-w-full">
              {user.name}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-white/70">
            {user.designation && (
              <span className="text-white/90 font-medium">{user.designation}</span>
            )}
            {cobranded && (
              <>
                <span className="text-white/40">at</span>
                <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-white/90 hover:text-white">
                  <Building2 size={12} />
                  <span className="font-semibold">{org.brand_name || org.legal_name}</span>
                </Link>
                {verified && (
                  <span className="inline-flex items-center gap-1 text-emerald-300 text-[10px] tracking-[0.2em] uppercase"
                        data-testid="cobrand-verified">
                    <CheckCircle2 size={11} /> Verified
                  </span>
                )}
                {pending && (
                  <span className="inline-flex items-center gap-1 text-amber-300 text-[10px] tracking-[0.2em] uppercase"
                        data-testid="cobrand-pending">
                    <Clock3 size={11} /> Pending Verification
                  </span>
                )}
              </>
            )}
            {!cobranded && (
              <span className="inline-flex items-center gap-1 text-white/60 text-[10px] tracking-[0.2em] uppercase"
                    data-testid="cobrand-freelance">
                Freelance
              </span>
            )}
          </div>
        </div>

        {/* Public link share + open buttons */}
        <div className="flex items-center gap-2">
          {handle && (
            <button type="button"
              onClick={copyLink}
              data-testid="crew-copy-public-link"
              title={`/u/${handle}`}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/15 text-[10px] tracking-[0.25em] uppercase text-white/70 hover:text-white hover:border-white/40">
              <LinkIcon size={11} /> Copy Link
            </button>
          )}
          {handle && (
            <Link
              to={`/u/${handle}`}
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/15 text-[10px] tracking-[0.25em] uppercase text-white/70 hover:text-white hover:border-white/40"
              data-testid="crew-view-public"
            >
              View Public Profile <ExternalLink size={11} />
            </Link>
          )}
        </div>
      </div>
      {archetype.tagline && (
        <div className="mt-4 text-[11px] tracking-[0.18em] uppercase text-white/45">
          {archetype.tagline}
        </div>
      )}
    </div>
  );
}

function WorkflowGrid({ widgets, accent, counts }) {
  if (!widgets?.length) {
    return (
      <div className="cs-card rounded-2xl p-6 text-center text-sm text-white/50"
           data-testid="crew-workflow-empty">
        Workflow modules for your role are being tuned. Check back shortly.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
         data-testid="crew-workflow-grid">
      {widgets.map((w) => {
        const IconEl = w.icon;
        const count  = counts ? counts[w.id] : undefined;
        // count === null means backend doesn't track this yet; hide badge.
        // count === 0/positive integer renders a chip.
        const showBadge = counts && typeof count === "number";
        return (
          <Link to={w.href} key={w.id}
            data-testid={`crew-widget-${w.id}`}
            className="cs-card rounded-xl p-5 hover:border-white/30 transition group">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                   style={{ background: `${accent}22`, color: accent }}>
                <IconEl size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] tracking-[0.24em] uppercase text-white/45">Module</div>
                <div className="text-sm font-semibold text-white truncate">{w.label}</div>
              </div>
              {showBadge && (
                <span
                  data-testid={`crew-count-${w.id}`}
                  className={`inline-flex items-center h-6 px-2 rounded-full text-[10px] tracking-[0.2em] uppercase font-semibold ${
                    count > 0
                      ? "text-black"
                      : "border border-white/12 text-white/45"
                  }`}
                  style={count > 0 ? { background: accent } : {}}
                >
                  {count > 0 ? count : "Empty"}
                </span>
              )}
            </div>
            {w.hint && (
              <div className="mt-3 text-[11px] text-white/55 leading-snug">{w.hint}</div>
            )}
            <div className="mt-4 text-[10px] tracking-[0.22em] uppercase group-hover:text-white text-white/45">
              Open →
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function QuickActionStrip({ actions, accent }) {
  if (!actions?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-6" data-testid="crew-quick-actions">
      {actions.map((a) => (
        <Link key={a.id} to={a.href}
          data-testid={`crew-quick-${a.id}`}
          className={`inline-flex items-center h-10 px-4 rounded-full text-[10px] tracking-[0.24em] uppercase font-semibold transition ${
            a.primary
              ? "text-black shadow-md"
              : "border border-white/15 text-white/75 hover:text-white hover:border-white/40"
          }`}
          style={a.primary ? { background: accent } : {}}>
          {a.label}
        </Link>
      ))}
    </div>
  );
}

export default function CrewShell({ archetype }) {
  const { user }        = useAuth();
  const [tab, setTab]   = useState("workflow");
  const [org, setOrg]   = useState(null);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      // Widget counts run for every user in parallel with org fetch.
      const jobs = [
        api.get("/dashboard/widget-counts")
           .then((r) => alive && setCounts(r.data.counts || {}))
           .catch(() => alive && setCounts({})),
      ];
      if (user?.organization_id) {
        jobs.push(
          api.get("/onboard/me/organization")
             .then((r) => alive && setOrg(r.data.organization || null))
             .catch(() => alive && setOrg(null))
        );
      } else {
        setOrg(null);
      }
      await Promise.all(jobs);
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [user?.organization_id]);

  const widgets = useMemo(() => {
    if (archetype.workflowByDept && user?.department_key) {
      return archetype.workflowByDept[user.department_key]
        || archetype.workflow
        || [];
    }
    return archetype.workflow || [];
  }, [archetype, user?.department_key]);

  if (!user) return null;
  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 text-white/50 text-sm"
           data-testid="crew-shell-loading">Loading your console…</div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid={`crew-shell-${archetype.key}`}>
      <CobrandStrip archetype={archetype} user={user} org={org} />

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6" data-testid="crew-tabs">
        {[
          { k: "workflow", l: "Workflow" },
          { k: "profile",  l: "Public Profile" },
        ].map((t) => (
          <button key={t.k} type="button"
            onClick={() => setTab(t.k)}
            data-testid={`crew-tab-${t.k}`}
            className={`h-9 px-4 rounded-full text-[10px] tracking-[0.24em] uppercase font-semibold transition ${
              tab === t.k
                ? "bg-white text-black"
                : "border border-white/15 text-white/60 hover:text-white hover:border-white/40"
            }`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "workflow" && (
        <>
          <WorkflowGrid widgets={widgets} accent={archetype.accent} counts={counts} />
          <QuickActionStrip actions={archetype.quickActions} accent={archetype.accent} />
        </>
      )}

      {tab === "profile" && (
        <div className="cs-card cs-grain rounded-2xl p-6 sm:p-8" data-testid="crew-public-profile">
          <PublicProfileCard user={user} />
        </div>
      )}
    </div>
  );
}

